<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="car-crash-page">
    <v-alert type="info" outlined dense class="mb-4">
      Potrzebujesz obsłużyć szkodę nieruchomości, sprzętu, mienia lub OC?
      <v-btn text small color="primary" class="ml-2" @click="$router.push('/claims')">Otwórz Likwidację szkód V1 <v-icon right small>mdi-arrow-right</v-icon></v-btn>
    </v-alert>
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Szkody</h1>
      </div>
    </div>
    <v-dialog content-class="dark-form-dialog" v-model="dialogCreate" max-width="900px" persistent>
      <v-card class="crash-wizard">
        <v-card-title class="crash-wizard-title">
          <v-icon left dark>mdi-clipboard-edit-outline</v-icon>
          {{ $t('crashes.addingCrash') }} — {{ wizardStepLabel }}
          <v-spacer />
          <v-btn icon dark @click="closeWizard"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-stepper v-model="wizardStep" alt-labels flat class="crash-stepper">
          <v-stepper-header>
            <v-stepper-step :complete="wizardStep > 1" step="1" editable :rules="[]">{{ $t('crashes.category') }}</v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="wizardStep > 2" step="2" editable :rules="[]">{{ $t('crashes.vehicleAndDriver') }}</v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="wizardStep > 3" step="3" editable :rules="[]">{{ $t('crashes.insurer') }}</v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="wizardStep > 4" step="4" editable :rules="[]">{{ $t('crashes.description') }}</v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="wizardStep > 5" step="5" editable :rules="[]">{{ $t('crashes.documents') }}</v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="wizardStep > 6" step="6" editable :rules="[]">{{ $t('crashes.summary') }}</v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <!-- ── Krok 1: Kategoria ── -->
            <v-stepper-content step="1">
              <div class="wiz-hint">{{ $t('crashes.step1Hint') }}</div>
              <v-row dense>
                <v-col cols="12">
                  <v-radio-group v-model="crash.category" row class="wiz-radio">
                    <v-radio
                      v-for="opt in categoryOptions"
                      :key="opt.value"
                      :label="opt.text"
                      :value="opt.value"
                    />
                  </v-radio-group>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="crash.date"
                    :label="$t('crashes.crashDate') + ' *'"
                    type="date"
                    outlined dense
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="crash.incidentNumber"
                    :label="$t('crashes.crashNumber')"
                    placeholder="np. 2026/04/001 (opcjonalne — można uzupełnić później)"
                    hint="Pole opcjonalne — uzupełnisz po otrzymaniu numeru z TU"
                    persistent-hint
                    outlined dense
                  />
                </v-col>
              </v-row>
            </v-stepper-content>

            <!-- ── Krok 2: Pojazd + Kierowca ── -->
            <v-stepper-content step="2">
              <div class="wiz-hint">{{ crash.category === 'rzeczowe' ? 'Opisz, czego dotyczy szkoda. Kierowca i pojazd są opcjonalne.' : $t('crashes.step2Hint') }}</div>
              <v-row dense>
                <v-col v-if="crash.category === 'rzeczowe'" cols="12">
                  <v-text-field
                    v-model="crash.subject"
                    label="Czego dotyczy szkoda *"
                    placeholder="np. szyba w biurze, telefon służbowy, ogrodzenie"
                    outlined dense
                    prepend-inner-icon="mdi-package-variant"
                  />
                </v-col>
                <v-col cols="12">
                  <!-- Combobox: wybór z listy kierowców firmy ALBO wpisanie wolnym tekstem (np. zewnętrzny). -->
                  <v-combobox
                    v-model="driverInput"
                    :items="driverOptions"
                    :label="$t('common.driver')"
                    :hint="'Wybierz z listy lub wpisz imię i nazwisko ręcznie'"
                    persistent-hint
                    outlined dense
                    clearable
                    prepend-inner-icon="mdi-account"
                  />
                </v-col>
                <v-col cols="12">
                  <!-- Combobox: pojazd z bazy ALBO wolny tekst (rejestracja). -->
                  <v-combobox
                    v-model="carInput"
                    :items="carOptions"
                    :label="$t('crashes.vehicleOptional')"
                    :hint="'Wybierz z floty lub wpisz numer rejestracyjny ręcznie'"
                    persistent-hint
                    outlined dense
                    clearable
                    prepend-inner-icon="mdi-car"
                  />
                </v-col>
              </v-row>
            </v-stepper-content>

            <!-- ── Krok 3: Ubezpieczyciel ── -->
            <v-stepper-content step="3">
              <div class="wiz-hint">{{ $t('crashes.step3Hint') }}</div>
              <v-text-field
                v-model="crash.insuranceInfo"
                :label="$t('crashes.insurerData') + ' *'"
                placeholder="np. PZU, polisa AC nr 123456"
                outlined dense
                prepend-inner-icon="mdi-shield-check-outline"
              />
            </v-stepper-content>

            <!-- ── Krok 4: Opis ── -->
            <v-stepper-content step="4">
              <div class="wiz-hint">{{ $t('crashes.step4Hint') }}</div>
              <v-textarea
                v-model="crash.description"
                :label="$t('crashes.incidentDescription') + ' *'"
                :placeholder="$t('crashes.descriptionPlaceholder')"
                outlined
                rows="6"
                auto-grow
              />
            </v-stepper-content>

            <!-- ── Krok 5: Dokumenty / zdjęcia ── -->
            <v-stepper-content step="5">
              <div class="wiz-hint">{{ $t('crashes.step5Hint') }}</div>
              <v-file-input
                v-model="crash.photos"
                :label="$t('crashes.files')"
                multiple outlined
                accept="image/*,.pdf"
                prepend-icon="mdi-paperclip"
                @change="previewFiles"
              />
              <div v-if="crash.previews && crash.previews.length > 0" class="mt-3">
                <h4>{{ $t('common.preview') }}:</h4>
                <v-row>
                  <v-col v-for="(preview, index) in crash.previews" :key="index" cols="6" md="4">
                    <v-card outlined>
                      <v-card-text class="text-center pa-2">
                        <img v-if="preview.type === 'image'" :src="preview.url" style="max-width:100%; max-height:160px;" />
                        <iframe v-else-if="preview.type === 'pdf'" :src="preview.url" style="width:100%; height:160px;" frameborder="0"></iframe>
                        <v-icon v-else size="48">mdi-file</v-icon>
                        <div class="mt-2 text-caption">{{ preview.name }}</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </v-stepper-content>

            <!-- ── Krok 6: Podsumowanie ── -->
            <v-stepper-content step="6">
              <div class="wiz-hint">{{ $t('crashes.step6Hint') }}</div>
              <div class="wiz-summary">
                <div class="wiz-summary-row">
                  <span class="wiz-summary-label">{{ $t('crashes.category') }}</span>
                  <span class="wiz-summary-value">{{ categoryLabel(crash.category) }}</span>
                </div>
                <div class="wiz-summary-row">
                  <span class="wiz-summary-label">{{ $t('crashes.crashDate') }}</span>
                  <span class="wiz-summary-value">{{ crash.date || '—' }}</span>
                </div>
                <div class="wiz-summary-row">
                  <span class="wiz-summary-label">{{ $t('crashes.crashNumber') }}</span>
                  <span class="wiz-summary-value">{{ crash.incidentNumber || '—' }}</span>
                </div>
                <div v-if="crash.category === 'rzeczowe'" class="wiz-summary-row">
                  <span class="wiz-summary-label">Czego dotyczy</span>
                  <span class="wiz-summary-value">{{ crash.subject || '—' }}</span>
                </div>
                <div class="wiz-summary-row">
                  <span class="wiz-summary-label">{{ $t('common.driver') }}</span>
                  <span class="wiz-summary-value">{{ driverDisplay(crash.driverId) }}</span>
                </div>
                <div class="wiz-summary-row">
                  <span class="wiz-summary-label">{{ $t('crashes.vehicle') }}</span>
                  <span class="wiz-summary-value">{{ carDisplay(carId) }}</span>
                </div>
                <div class="wiz-summary-row">
                  <span class="wiz-summary-label">{{ $t('crashes.insurer') }}</span>
                  <span class="wiz-summary-value">{{ crash.insuranceInfo || '—' }}</span>
                </div>
                <div class="wiz-summary-row wiz-summary-row--block">
                  <span class="wiz-summary-label">{{ $t('crashes.description') }}</span>
                  <div class="wiz-summary-desc">{{ crash.description || '—' }}</div>
                </div>
                <div class="wiz-summary-row">
                  <span class="wiz-summary-label">{{ $t('crashes.attachments') }}</span>
                  <span class="wiz-summary-value">{{ (crash.photos && crash.photos.length) || 0 }} plik(ów)</span>
                </div>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>

        <v-card-actions class="crash-wizard-actions">
          <v-btn text @click="closeWizard">{{ $t('common.cancel') }}</v-btn>
          <v-spacer />
          <v-btn outlined :disabled="wizardStep <= 1" @click="wizardStep--">
            <v-icon small left>mdi-chevron-left</v-icon>{{ $t('common.previous') }}
          </v-btn>
          <v-btn
            v-if="wizardStep < 6"
            depressed color="primary"
            :disabled="!canGoNext"
            @click="wizardStep++"
          >
            {{ $t('common.next') }}<v-icon small right>mdi-chevron-right</v-icon>
          </v-btn>
          <v-btn
            v-else
            depressed color="primary"
            :loading="savingWizard"
            @click="saveCrash"
          >
            <v-icon small left>mdi-content-save</v-icon>{{ $t('crashes.saveCrash') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Zakładki główne: Firmowe / Cesje / Zewnętrzne / Kalkulator -->
    <v-tabs v-model="activeTab" class="crashes-tabs mb-4" grow>
      <v-tab>
        <v-icon left>mdi-domain</v-icon>
        {{ $t('crashes.company') }}
        <span class="crashes-tab-count">{{ countByCategory('firmowe') }}</span>
      </v-tab>
      <v-tab>
        <v-icon left>mdi-swap-horizontal</v-icon>
        {{ $t('crashes.cessions') }}
        <span class="crashes-tab-count">{{ countByCategory('cesje') }}</span>
      </v-tab>
      <v-tab>
        <v-icon left>mdi-package-variant</v-icon>
        Rzeczowe
        <span class="crashes-tab-count">{{ countByCategory('rzeczowe') }}</span>
      </v-tab>
      <v-tab>
        <v-icon left>mdi-calculator-variant</v-icon>
        {{ $t('crashes.rentCalculator') }}
      </v-tab>
      <v-tab>
        <v-icon left>mdi-file-document-edit</v-icon>
        Generator dokumentów
      </v-tab>
      <v-tab>
        <v-icon left>mdi-archive-search</v-icon>
        Repozytorium
        <span class="crashes-tab-count">{{ generatedDocs.length }}</span>
      </v-tab>
      <v-tab>
        <v-icon left>mdi-bell-alert</v-icon>
        Alerty „Mam kolizję"
        <span class="crashes-tab-count crashes-tab-count--alert" v-if="newAlertsCount > 0">
          {{ newAlertsCount }}
        </span>
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" class="crashes-tabs-items" touchless>
      <!-- ── Zakładka: Firmowe ── -->
      <v-tab-item>
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="filteredCrashes('firmowe')"
          class="elevation-1"
          :loading="loading"
                    :search="search"
          :options.sync="options"
          :server-items-length="totalItems"
        >
          <template v-slot:top>
            <div class="table-section-title">
              <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-car-emergency</v-icon></div>
              <div class="filter-title">{{ $t('crashes.companyCrashes') }}</div>
              <v-spacer></v-spacer>
              <button class="action-pill action-pill--primary" @click="openCreateForCategory('firmowe')">
                <v-icon left small>mdi-plus</v-icon>
                {{ $t('common.add') }}
              </button>
            </div>
                    <div>
              <v-text-field
                v-model="search"
                :label="$t('common.search')"
                class="mx-4"
              ></v-text-field>
            </div>
          </template>
          <template v-slot:item.details="{ item }">
            <v-btn class="warning" @click="infoCrash(item.id)"
              ><v-icon small class="pt-1">mdi-pencil</v-icon></v-btn
            >
          </template>
          <template v-slot:item.timeline="{ item }">
            <v-btn class="info" @click="timelineCrash(item.id)"
              ><v-icon small class="pt-1">mdi-archive-clock</v-icon></v-btn
            >
          </template>
          <template v-slot:item.download="{ item }">
            <v-btn
              v-if="item.hasFiles"
              class="secondary"
              @click="downloadCrashFiles(item.id)"
            >
              Pobierz pliki
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
      </v-tab-item>

      <!-- ── Zakładka: Cesje ── -->
      <v-tab-item>
        <v-row>
          <v-col cols="12">
            <v-data-table
              :headers="headers"
              :items="filteredCrashes('cesje')"
              class="elevation-1"
              :loading="loading"
              :search="search"
              :options.sync="options"
              :server-items-length="countByCategory('cesje')"
            >
              <template v-slot:top>
                <div class="table-section-title">
                  <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-clipboard-arrow-right</v-icon></div>
                  <div class="filter-title">{{ $t('crashes.cessions') }}</div>
                  <v-spacer></v-spacer>
                  <button class="action-pill action-pill--primary" @click="openCreateForCategory('cesje')">
                    <v-icon left small>mdi-plus</v-icon>
                    {{ $t('common.add') }}
                  </button>
                </div>
                <div>
                  <v-text-field v-model="search" :label="$t('common.search')" class="mx-4"></v-text-field>
                </div>
              </template>
              <template v-slot:item.details="{ item }">
                <v-btn class="warning" @click="infoCrash(item.id)"><v-icon small class="pt-1">mdi-pencil</v-icon></v-btn>
              </template>
              <template v-slot:item.timeline="{ item }">
                <v-btn class="info" @click="timelineCrash(item.id)"><v-icon small class="pt-1">mdi-archive-clock</v-icon></v-btn>
              </template>
              <template v-slot:item.download="{ item }">
                <v-btn v-if="item.hasFiles" class="secondary" @click="downloadCrashFiles(item.id)">{{ $t('crashes.downloadFiles') }}</v-btn>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- ── Zakładka: Rzeczowe (szkoda nie na aucie) ── -->
      <v-tab-item>
        <v-row>
          <v-col cols="12">
            <v-data-table
              :headers="headersRzeczowe"
              :items="filteredCrashes('rzeczowe')"
              class="elevation-1"
              :loading="loading"
              :search="search"
              :options.sync="options"
              :server-items-length="countByCategory('rzeczowe')"
            >
              <template v-slot:top>
                <div class="table-section-title">
                  <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-package-variant</v-icon></div>
                  <div class="filter-title">Szkody rzeczowe</div>
                  <v-spacer></v-spacer>
                  <button class="action-pill action-pill--primary" @click="openCreateForCategory('rzeczowe')">
                    <v-icon left small>mdi-plus</v-icon>
                    {{ $t('common.add') }}
                  </button>
                </div>
                <div>
                  <v-text-field v-model="search" :label="$t('common.search')" class="mx-4"></v-text-field>
                </div>
              </template>
              <template v-slot:item.details="{ item }">
                <v-btn class="warning" @click="infoCrash(item.id)"><v-icon small class="pt-1">mdi-pencil</v-icon></v-btn>
              </template>
              <template v-slot:item.timeline="{ item }">
                <v-btn class="info" @click="timelineCrash(item.id)"><v-icon small class="pt-1">mdi-archive-clock</v-icon></v-btn>
              </template>
              <template v-slot:item.download="{ item }">
                <v-btn v-if="item.hasFiles" class="secondary" @click="downloadCrashFiles(item.id)">{{ $t('crashes.downloadFiles') }}</v-btn>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- ── Zakładka: Kalkulator najmu ── -->
      <v-tab-item>
        <v-card class="rent-calc mb-4">
          <v-card-title class="rent-calc-title">
            <v-icon left color="primary">mdi-calculator-variant</v-icon>
            {{ $t('crashes.rentCalculatorTitle') }}
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="rentCalc.plate"
                  :label="$t('crashes.registrationNumber')"
                  placeholder="np. WA12345"
                  outlined dense hide-details
                  prepend-inner-icon="mdi-car"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="rentCalc.crashNumber"
                  :label="$t('crashes.crashNumber')"
                  placeholder="np. 2026/04/001"
                  outlined dense hide-details
                  prepend-inner-icon="mdi-file-document-alert-outline"
                />
              </v-col>
            </v-row>

            <v-row align="center" class="mt-4">
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="rentCalc.from"
                  type="date"
                  label="Od"
                  outlined dense hide-details
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="rentCalc.to"
                  type="date"
                  label="Do"
                  outlined dense hide-details
                  :min="rentCalc.from || undefined"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="rentCalc.dailyRate"
                  type="text"
                  inputmode="decimal"
                  placeholder="np. 150"
                  :label="$t('crashes.dailyRate')"
                  outlined dense hide-details
                  suffix="zł"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model.number="rentCalc.vehicles"
                  type="number"
                  min="1"
                  step="1"
                  :label="$t('crashes.vehicleCount')"
                  outlined dense hide-details
                />
              </v-col>
            </v-row>

            <v-row dense class="mt-4">
              <v-col cols="12" md="4">
                <div class="rent-calc-kpi">
                  <div class="rent-calc-kpi-label">{{ $t('crashes.dayCount') }}</div>
                  <div class="rent-calc-kpi-value">{{ rentDays }}</div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="rent-calc-kpi">
                  <div class="rent-calc-kpi-label">{{ $t('crashes.amountPerVehicle') }}</div>
                  <div class="rent-calc-kpi-value">{{ formatPln(rentPerVehicle) }}</div>
                  <div class="rent-calc-kpi-sub">{{ rentDays }} dni × {{ formatPln(parsedRate) }}</div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="rent-calc-kpi rent-calc-kpi--total">
                  <div class="rent-calc-kpi-label">Suma ({{ rentCalc.vehicles }} {{ rentCalc.vehicles === 1 ? 'pojazd' : 'pojazdów' }})</div>
                  <div class="rent-calc-kpi-value">{{ formatPln(rentTotal) }}</div>
                </div>
              </v-col>
            </v-row>

            <div v-if="rentError" class="rent-calc-error mt-3">
              <v-icon small color="error">mdi-alert-outline</v-icon>
              {{ rentError }}
            </div>

            <div class="rent-calc-actions mt-3">
              <v-btn small text @click="resetRentCalc">
                <v-icon small left>mdi-backup-restore</v-icon>{{ $t('crashes.clear') }}
              </v-btn>
              <v-btn small outlined color="primary" :disabled="!rentValid" @click="copyRentSummary">
                <v-icon small left>mdi-content-copy</v-icon>{{ $t('crashes.copy') }}
              </v-btn>
              <v-btn small depressed color="primary" :disabled="!rentValid" @click="saveRentCalc">
                <v-icon small left>mdi-content-save</v-icon>{{ $t('crashes.saveCalculation') }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <!-- Historia obliczeń -->
        <v-card class="rent-history">
          <v-card-title class="rent-history-head">
            <v-icon left color="primary">mdi-history</v-icon>
            {{ $t('crashes.calculationHistory') }}
            <v-chip small class="ml-2">{{ rentHistory.length }}</v-chip>
            <v-spacer />
            <v-text-field
              v-model="historySearch"
              dense outlined hide-details
              prepend-inner-icon="mdi-magnify"
              :placeholder="$t('crashes.searchByPlateOrCrash')"
              style="max-width: 320px"
              clearable
            />
            <v-btn v-if="rentHistory.length" small text color="error" class="ml-2" @click="clearRentHistory">
              <v-icon small left>mdi-delete-sweep</v-icon>{{ $t('crashes.clear') }}
            </v-btn>
          </v-card-title>

          <div v-if="filteredHistory.length === 0" class="rent-history-empty">
            <v-icon large color="grey lighten-1">mdi-calculator-off</v-icon>
            <div>{{ rentHistory.length === 0 ? 'Brak zapisanych obliczeń' : 'Brak wyników dla wyszukiwania' }}</div>
          </div>

          <v-simple-table v-else dense class="rent-history-table">
            <thead>
              <tr>
                <th>{{ $t('crashes.savedDate') }}</th>
                <th>{{ $t('crashes.plateOrCrashNo') }}</th>
                <th>{{ $t('crashes.period') }}</th>
                <th class="text-right">{{ $t('crashes.days') }}</th>
                <th class="text-right">{{ $t('crashes.rate') }}</th>
                <th class="text-center">{{ $t('crashes.vehicles') }}</th>
                <th class="text-right">{{ $t('crashes.total') }}</th>
                <th style="width: 140px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="h in filteredHistory" :key="h.id">
                <td class="rent-history-date">{{ formatSavedDate(h.savedAt) }}</td>
                <td>
                  <div v-if="h.plate" class="rent-history-tag"><v-icon x-small>mdi-car</v-icon> {{ h.plate }}</div>
                  <div v-if="h.crashNumber" class="rent-history-tag rent-history-tag--muted">
                    <v-icon x-small>mdi-file-document-alert-outline</v-icon> {{ h.crashNumber }}
                  </div>
                  <span v-if="!h.plate && !h.crashNumber" class="grey--text">—</span>
                </td>
                <td class="rent-history-period">{{ h.from }} → {{ h.to }}</td>
                <td class="text-right">{{ h.days }}</td>
                <td class="text-right">{{ formatPln(h.dailyRate) }}</td>
                <td class="text-center">{{ h.vehicles }}</td>
                <td class="text-right rent-history-sum">{{ formatPln(h.total) }}</td>
                <td class="text-right">
                  <v-btn icon x-small @click="loadHistoryEntry(h)" title="Wczytaj do kalkulatora">
                    <v-icon x-small>mdi-upload</v-icon>
                  </v-btn>
                  <v-btn icon x-small @click="copyHistoryEntry(h)" title="Skopiuj podsumowanie">
                    <v-icon x-small>mdi-content-copy</v-icon>
                  </v-btn>
                  <v-btn icon x-small @click="deleteHistoryEntry(h.id)" title="Usuń">
                    <v-icon x-small color="error">mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </v-tab-item>

      <!-- ── Zakładka: Generator dokumentów ── -->
      <v-tab-item>
        <v-card outlined class="rounded-lg pa-4" style="overflow: hidden;">
          <v-card-title>
            <v-icon left color="primary">mdi-file-document-edit</v-icon>
            Generator dokumentów (MVP)
            <v-spacer />
            <v-chip x-small color="orange" text-color="white">na razie tylko: Cesja</v-chip>
          </v-card-title>
          <v-divider class="mb-4" />

          <v-stepper v-model="genStep" vertical flat>
            <v-stepper-step :complete="genStep > 1" step="1" editable :rules="[]">
              Typ sprawy
              <small>Cesja albo Firmowa</small>
            </v-stepper-step>
            <v-stepper-content step="1">
              <v-radio-group v-model="genForm.caseType" row>
                <v-radio label="Cesja" value="CESJA" />
                <v-radio label="Firmowa" value="FIRMOWA" />
              </v-radio-group>
              <v-btn color="primary" small :disabled="!genForm.caseType" @click="genStep = 2">Dalej</v-btn>
            </v-stepper-content>

            <v-stepper-step :complete="genStep > 2" step="2" editable :rules="[]">
              Dane stron
              <small>Najemca, auto, daty</small>
            </v-stepper-step>
            <v-stepper-content step="2">
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field v-model="genForm.cedent" label="Cedent / Najemca (klient)" outlined dense />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="genForm.city" label="Miasto" placeholder="Bydgoszcz" outlined dense />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model="genForm.documentDate" label="Data wystawienia" type="date" outlined dense />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model="genForm.signedDate" label="Data zawarcia" type="date" outlined dense />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model="genForm.damageDate" label="Data szkody" type="date" outlined dense />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model="genForm.vehicleBrand" label="Marka pojazdu" outlined dense />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model="genForm.registrationNumber" label="Nr rejestracyjny" outlined dense />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field v-model="genForm.rentalAgreementNumber" label="Nr umowy najmu" outlined dense />
                </v-col>
              </v-row>
              <div class="mt-3">
                <v-btn text small @click="genStep = 1">Wstecz</v-btn>
                <v-btn color="primary" small @click="genStep = 3">Dalej</v-btn>
              </div>
            </v-stepper-content>

            <v-stepper-step :complete="genStep > 3" step="3" editable :rules="[]">
              Wybór dokumentów
              <small>Co wygenerować</small>
            </v-stepper-step>
            <v-stepper-content step="3">
              <div class="caption grey--text mb-2">Wybierz dokumenty do wygenerowania. „Wkrótce" = brak treści szablonu.</div>
              <v-checkbox v-model="genForm.types" value="CESJA" label="Cesja praw" hide-details />
              <v-checkbox disabled value="PELNOMOCNICTWO" label="Pełnomocnictwo do odbioru odszkodowania (wkrótce)" hide-details />
              <v-checkbox disabled value="OSW_OFERTA_NAJMU" label="Oświadczenie o otrzymaniu oferty najmu (wkrótce)" hide-details />
              <v-checkbox disabled value="OSW_STAN_TECHNICZNY" label="Oświadczenie o stanie technicznym (wkrótce)" hide-details />
              <v-checkbox disabled value="OSW_DATA_ODSZKODOWANIA" label="Oświadczenie o dacie otrzymania odszkodowania (wkrótce)" hide-details />
              <v-checkbox disabled value="PROTOKOL_ZDAWCZO_ODBIORCZY" label="Protokół zdawczo-odbiorczy (wkrótce)" hide-details />
              <div class="mt-3">
                <v-btn text small @click="genStep = 2">Wstecz</v-btn>
                <v-btn color="primary" small :disabled="!(genForm.types && genForm.types.length)" @click="genStep = 4">Dalej</v-btn>
              </div>
            </v-stepper-content>

            <v-stepper-step :complete="genStep > 4" step="4" editable :rules="[]">
              Podgląd i generowanie
            </v-stepper-step>
            <v-stepper-content step="4">
              <v-alert outlined type="info" dense class="mb-3">
                Po kliknięciu „Generuj" — PDF zapisze się w repozytorium (zakładka obok). Status DRAFT — gotowy do podpisu.
              </v-alert>
              <v-simple-table dense>
                <tbody>
                  <tr><td><strong>Typ sprawy</strong></td><td>{{ genForm.caseType }}</td></tr>
                  <tr><td><strong>Cedent / Najemca</strong></td><td>{{ genForm.cedent || '—' }}</td></tr>
                  <tr><td><strong>Miasto</strong></td><td>{{ genForm.city || '—' }}</td></tr>
                  <tr><td><strong>Data wystawienia</strong></td><td>{{ genForm.documentDate || '—' }}</td></tr>
                  <tr><td><strong>Data zawarcia</strong></td><td>{{ genForm.signedDate || '—' }}</td></tr>
                  <tr><td><strong>Data szkody</strong></td><td>{{ genForm.damageDate || '—' }}</td></tr>
                  <tr><td><strong>Pojazd</strong></td><td>{{ genForm.vehicleBrand }} — {{ genForm.registrationNumber }}</td></tr>
                  <tr><td><strong>Nr umowy najmu</strong></td><td>{{ genForm.rentalAgreementNumber || '—' }}</td></tr>
                  <tr><td><strong>Dokumenty</strong></td><td>{{ (genForm.types || []).join(', ') }}</td></tr>
                </tbody>
              </v-simple-table>
              <div class="mt-3">
                <v-btn text small @click="genStep = 3">Wstecz</v-btn>
                <v-btn color="info" outlined :loading="loadingPreview" class="mr-2" @click="openPreview">
                  <v-icon left small>mdi-eye</v-icon>Podgląd
                </v-btn>
                <v-btn color="primary" :loading="generating" @click="submitGenerate">
                  <v-icon left small>mdi-file-pdf-box</v-icon>Generuj PDF
                </v-btn>
                <v-btn text small class="ml-2" @click="resetGenForm">Wyczyść</v-btn>
              </div>

              <!-- Inline podgląd PDF (po kliknięciu „Podgląd") — iframe na pełną szerokość -->
              <div v-if="previewUrl" class="mt-4">
                <iframe :src="previewUrl" style="width: 100%; height: 700px; border: 1px solid #ccc; border-radius: 8px;" />
              </div>
            </v-stepper-content>
          </v-stepper>
        </v-card>
      </v-tab-item>

      <!-- ── Zakładka: Repozytorium ── -->
      <v-tab-item>
        <v-card outlined class="rounded-lg" style="overflow: hidden;">
          <v-card-title>
            <v-icon left color="primary">mdi-archive-search</v-icon>
            Repozytorium podpisanych dokumentów
            <v-spacer />
            <v-btn x-small text :loading="loadingDocs" @click="loadGeneratedDocs">
              <v-icon x-small left>mdi-refresh</v-icon>Odśwież
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-row dense class="mb-2">
              <v-col cols="6" md="3">
                <v-select
                  v-model="docFilters.caseType"
                  :items="[{ text: 'Wszystkie', value: '' }, { text: 'Cesja', value: 'CESJA' }, { text: 'Firmowa', value: 'FIRMOWA' }]"
                  label="Typ sprawy"
                  outlined dense hide-details
                  @change="loadGeneratedDocs"
                />
              </v-col>
              <v-col cols="6" md="3">
                <v-select
                  v-model="docFilters.status"
                  :items="[{ text: 'Wszystkie', value: '' }, { text: 'DRAFT', value: 'DRAFT' }, { text: 'Podpisany przez admina', value: 'SIGNED_ADMIN' }, { text: 'Podpisany przez obie strony', value: 'SIGNED_BOTH' }]"
                  label="Status"
                  outlined dense hide-details
                  @change="loadGeneratedDocs"
                />
              </v-col>
            </v-row>

            <v-data-table
              :headers="docHeaders"
              :items="generatedDocs"
              :loading="loadingDocs"
              class="elevation-0"
              dense
              no-data-text="Brak dokumentów. Wygeneruj pierwszy w zakładce Generator dokumentów."
            >
              <template v-slot:[`item.createdAt`]="{ item }">
                {{ new Date(item.createdAt).toLocaleString('pl-PL') }}
              </template>
              <template v-slot:[`item.type`]="{ item }">
                <v-chip x-small color="blue lighten-5" text-color="blue darken-3">{{ item.type }}</v-chip>
              </template>
              <template v-slot:[`item.caseType`]="{ item }">
                <v-chip x-small :color="item.caseType === 'CESJA' ? 'orange lighten-5' : 'green lighten-5'" :text-color="item.caseType === 'CESJA' ? 'orange darken-3' : 'green darken-3'">
                  {{ item.caseType }}
                </v-chip>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip x-small :color="docStatusColor(item.status)" text-color="white">{{ item.status }}</v-chip>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn x-small color="info" class="mr-1" @click="openDocPreview(item)">
                  <v-icon x-small left>mdi-eye</v-icon>Podgląd
                </v-btn>
                <v-btn x-small color="warning" class="mr-1" @click="openSignDialog(item, 'admin')">
                  <v-icon x-small left>mdi-draw</v-icon>Admin
                </v-btn>
                <v-btn x-small color="success" class="mr-1" @click="openSignDialog(item, 'client')">
                  <v-icon x-small left>mdi-tablet-android</v-icon>Klient (tablet)
                </v-btn>
                <v-btn x-small color="primary" class="mr-1" @click="downloadDoc(item)">
                  <v-icon x-small>mdi-download</v-icon>
                </v-btn>
                <v-btn x-small color="error" outlined @click="deleteDoc(item)">
                  <v-icon x-small>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- ── Zakładka: Alerty „Mam kolizję" ── -->
      <v-tab-item>
        <v-card outlined class="rounded-lg">
          <v-card-title class="d-flex align-center">
            <v-icon left color="error">mdi-bell-alert</v-icon>
            Alerty „Mam kolizję"
            <v-spacer />
            <v-select
              v-model="alertsFilter.status"
              :items="alertStatusOptions"
              item-text="text"
              item-value="value"
              label="Status"
              outlined dense hide-details
              clearable
              style="max-width: 220px"
              @change="loadCrashAlerts"
            />
            <v-btn icon @click="loadCrashAlerts" :loading="loadingAlerts" class="ml-2" title="Odśwież">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-2">
            <v-data-table
              :headers="alertHeaders"
              :items="crashAlerts"
              :loading="loadingAlerts"
              :items-per-page="20"
              dense
              no-data-text="Brak alertów"
              class="elevation-0"
            >
              <template v-slot:[`item.createdAt`]="{ item }">
                {{ formatDateTime(item.createdAt) }}
              </template>
              <template v-slot:[`item.driver`]="{ item }">
                <div v-if="item.driver">
                  <strong>{{ item.driver.firstName }} {{ item.driver.lastName }}</strong>
                  <div class="caption grey--text">
                    <a v-if="item.driver.phone" :href="`tel:${item.driver.phone}`">
                      <v-icon x-small>mdi-phone</v-icon> {{ item.driver.phone }}
                    </a>
                  </div>
                </div>
                <span v-else class="grey--text">—</span>
              </template>
              <template v-slot:[`item.car`]="{ item }">
                <div v-if="item.car">
                  <strong>{{ item.car.plate }}</strong>
                  <div class="caption grey--text">{{ item.car.make }} {{ item.car.model }}</div>
                </div>
                <span v-else class="grey--text">—</span>
              </template>
              <template v-slot:[`item.note`]="{ item }">
                <span v-if="item.note" class="caption">{{ item.note }}</span>
                <span v-else class="caption grey--text">—</span>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip x-small :color="alertStatusColor(item.status)" dark>
                  {{ alertStatusLabel(item.status) }}
                </v-chip>
                <div v-if="item.contactedAt" class="caption grey--text mt-1">
                  Kontakt: {{ formatDateTime(item.contactedAt) }}
                </div>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex" style="gap: 4px; flex-wrap: wrap;">
                  <v-btn
                    v-if="item.status === 'NEW'"
                    x-small color="info"
                    @click="setAlertStatus(item, 'CONTACTED')"
                    title="Oznacz jako skontaktowany"
                  >
                    <v-icon x-small left>mdi-phone-check</v-icon>Skontaktowano
                  </v-btn>
                  <v-btn
                    v-if="item.status !== 'CLOSED' && item.status !== 'CONVERTED'"
                    x-small color="success"
                    @click="setAlertStatus(item, 'CLOSED')"
                    title="Zamknij alert"
                  >
                    <v-icon x-small left>mdi-check</v-icon>Zamknij
                  </v-btn>
                  <v-btn
                    v-if="item.status !== 'CONVERTED'"
                    x-small color="warning"
                    @click="convertAlertToCrash(item)"
                    title="Otwórz pełne zgłoszenie szkody"
                  >
                    <v-icon x-small left>mdi-arrow-right-bold</v-icon>Konwertuj na szkodę
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- Dialog: podgląd PDF z repozytorium -->
    <v-dialog content-class="dark-form-dialog" v-model="docPreviewDialog" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left dark>mdi-file-eye</v-icon>
          Podgląd: {{ docPreviewItem ? docPreviewItem.number : '' }}
          <v-spacer />
          <v-btn icon dark @click="docPreviewDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-0" style="height: 80vh;">
          <iframe v-if="docPreviewSrc" :src="docPreviewSrc" style="width: 100%; height: 100%; border: 0;" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog: podpis (admin albo klient na tablecie) — pełnoekranowy canvas -->
    <v-dialog content-class="dark-form-dialog" v-model="signDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left dark>mdi-draw</v-icon>
          Podpis: {{ signParty === 'client' ? 'Klient (Cedent / Najemca)' : 'Admin (Cesjonariusz / Wynajmujący)' }}
          — {{ signItem ? signItem.number : '' }}
          <v-spacer />
          <v-btn icon dark @click="signDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-alert type="info" outlined dense class="mb-2">
            {{ signParty === 'client'
              ? 'Daj tablet osobie podpisującej. Niech podpisze się palcem na obszarze poniżej.'
              : 'Podpisz dokument jako administrator (Cesjonariusz / Wynajmujący).' }}
          </v-alert>
          <div style="border: 2px dashed #999; border-radius: 8px; touch-action: none;">
            <canvas
              ref="sigCanvas"
              width="900"
              height="350"
              style="display: block; width: 100%; height: 350px; background: #ffffff;"
              @mousedown="sigStart"
              @mousemove="sigDraw"
              @mouseup="sigStop"
              @mouseleave="sigStop"
              @touchstart.prevent="sigStartTouch"
              @touchmove.prevent="sigDrawTouch"
              @touchend.prevent="sigStop"
            ></canvas>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="clearSignature">
            <v-icon left>mdi-eraser</v-icon>Wyczyść
          </v-btn>
          <v-spacer />
          <v-btn text @click="signDialog = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="submittingSign" @click="submitSignature">
            <v-icon left>mdi-check</v-icon>Zapisz podpis
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Form Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h6">{{ $t('crashes.editCrash') }}</span>
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="valid" @submit.prevent="submitForm">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="formData.incidentDate"
                  :label="$t('crashes.crashDate')"
                  type="date"
                ></v-text-field>
              </v-col>
<v-col v-if="formData.category === 'rzeczowe'" cols="12">
                <v-text-field
                  v-model="formData.subject"
                  label="Czego dotyczy szkoda"
                ></v-text-field>
              </v-col>
<v-col cols="12">
                <v-text-field
                  v-model="formData.insuranceInfo"
                  :label="$t('crashes.insurerData')"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="formData.caseNumber"
                  :label="$t('crashes.crashNumber')"
                ></v-text-field>
              </v-col>
                                         <v-col cols="12">
                <v-textarea
                  v-model="formData.description"
                  :label="$t('fleet.notes')"
                ></v-textarea>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="formData.status"
                  :items="statusOptions"
                  :label="$t('crashes.crashStatus')"
                  outlined
                  required
                  item-text="text"
                  item-value="value"
                />
              </v-col>
              <v-col cols="12">
                <v-file-input
                  v-model="formData.newFiles"
                  :label="$t('crashes.addNewFiles')"
                  multiple
                  outlined
                  accept="image/*,.pdf"
                  @change="previewEditFiles"
                ></v-file-input>
                <div v-if="formData.newFilePreviews && formData.newFilePreviews.length > 0" class="mt-3">
                  <h4>{{ $t('crashes.newFilesPreview') }}:</h4>
                  <v-row>
                    <v-col v-for="(preview, index) in formData.newFilePreviews" :key="'new-' + index" cols="6" md="4">
                      <v-card>
                        <v-card-text class="text-center">
                          <img
                            v-if="preview.type === 'image'"
                            :src="preview.url"
                            style="max-width: 100%; max-height: 200px;"
                            alt="Preview"
                          />
                          <iframe
                            v-else-if="preview.type === 'pdf'"
                            :src="preview.url"
                            style="width: 100%; height: 200px"
                            frameborder="0"
                          ></iframe>
                          <v-icon v-else size="60">mdi-file</v-icon>
                          <div class="mt-2">{{ preview.name }}</div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
              <v-col cols="12" v-if="formData.files && formData.files.length > 0">
                <h4>{{ $t('crashes.existingFiles') }}:</h4>
                <v-row>
                  <v-col v-for="file in formData.files" :key="file.id" cols="6" md="4">
                    <v-card>
                      <v-card-text class="text-center">
                        <v-icon size="60">mdi-file</v-icon>
                        <div class="mt-2">{{ file.originalName || $t('crashes.file') }}</div>
                        <v-btn
                          small
                          color="primary"
                          @click="downloadSingleFile(file.id)"
                          class="mt-2"
                        >
                          {{ $t('common.download') }}
                        </v-btn>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col> 
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- Cancel Button -->
          <v-btn text color="secondary" @click="closeDialog">{{ $t('common.cancel') }}</v-btn>
          <!-- Submit Button -->
          <v-btn color="primary" @click="submitForm" :disabled="!valid">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");
import * as dayjs from "dayjs";

export default {
  data() {
    return {
      activeTab: 0,
      // ===== Alerty „Mam kolizję" =====
      crashAlerts: [],
      loadingAlerts: false,
      alertsFilter: { status: 'NEW' },
      alertHeaders: [
        { text: 'Zgłoszono', value: 'createdAt', width: '160px' },
        { text: 'Kierowca', value: 'driver' },
        { text: 'Auto', value: 'car' },
        { text: 'Opis', value: 'note' },
        { text: 'Status', value: 'status', width: '180px' },
        { text: 'Akcje', value: 'actions', sortable: false, width: '320px' },
      ],
      alertStatusOptions: [
        { text: 'Wszystkie', value: '' },
        { text: 'Nowe', value: 'NEW' },
        { text: 'Skontaktowano', value: 'CONTACTED' },
        { text: 'Zamknięte', value: 'CLOSED' },
        { text: 'Konwertowane', value: 'CONVERTED' },
      ],
      wizardStep: 1,
      savingWizard: false,
      rentCalc: { from: '', to: '', dailyRate: null, vehicles: 1, plate: '', crashNumber: '' },
      rentHistory: [],
      historySearch: '',
      driversEndRent:[],
      carId: null,
        totalItems:0,
        carSelect:[],
          search: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      carCrashes: [],
      headersRzeczowe: [
        { text: "Numer Szkody", align: "start", sortable: false, value: "caseNumber" },
        { text: "Data Szkody", align: "start", sortable: false, value: "incidentDate" },
        { text: "Status", align: "start", sortable: false, value: "status" },
        { text: "Czego dotyczy", align: "start", sortable: false, value: "subject" },
        { text: "", value: "details", sortable: false },
        { text: "", value: "timeline", sortable: false },
        { text: "", value: "download", sortable: false },
      ],
      headers: [
        {
          text: "Numer Szkody",
          align: "start",
          sortable: false,
          value: "caseNumber",
        },
        {
          text: "Data Szkody",
          align: "start",
          sortable: false,
          value: "incidentDate",
        },        {
          text: "Status",
          align: "start",
          sortable: false,
          value: "status",
        },
        {
          text: "Auto",
          align: "start",
          sortable: false,
          value: "carPlate",
        },
        { text: "", value: "details", sortable: false },
        { text: "", value: "timeline", sortable: false },
        { text: "", value: "download", sortable: false },
      ],
      loading: false,
      dialog: false,
      dialogCreate: false,
      valid: false,
      formData: {
        status: 'pending',
        description: null,
        incidentDate: null,
        caseNumber: null,
        insuranceInfo: null,
        newFiles: [],
        newFilePreviews: [],
        files: [],
      },
      statusOptions: [
        { text: "Oczekuje", value: "pending" },
        { text: "W trakcie 🔄", value: "in_progress" },
        { text: "Zlikwidowana ✅", value: "fixed" }
      ],
      crash: {
        photos: [],
        previews: [],
        insuranceInfo: "",
        date: "",
        fixed: false,
        fixedDate: "",
        incidentNumber: "",
        description: "",
        driverId: null,
        driverName: "",
        carPlate: "",
        category: "firmowe",
        subject: "",
      },
      categoryOptions: [
        { text: "Firmowe", value: "firmowe" },
        { text: "Cesje", value: "cesje" },
        { text: "Rzeczowe", value: "rzeczowe" },
      ],
      rules: {
        required: (value) => !!value || "Wymagane.",
      },
      // Generator dokumentów
      genStep: 1,
      generating: false,
      genForm: {
        caseType: 'CESJA',
        cedent: '',
        city: 'Bydgoszcz',
        documentDate: '',
        signedDate: '',
        damageDate: '',
        vehicleBrand: '',
        registrationNumber: '',
        rentalAgreementNumber: '',
        types: ['CESJA'],
      },
      // Repozytorium
      generatedDocs: [],
      loadingDocs: false,
      docFilters: { caseType: '', status: '' },
      docHeaders: [
        { text: 'Numer', value: 'number' },
        { text: 'Typ', value: 'type' },
        { text: 'Sprawa', value: 'caseType' },
        { text: 'Status', value: 'status' },
        { text: 'Utworzono', value: 'createdAt' },
        { text: 'Akcje', value: 'actions', sortable: false, align: 'end' },
      ],
      // Podgląd PDF (krok 4 generatora — bez zapisu)
      previewUrl: null,
      loadingPreview: false,
      // Podgląd dokumentu z repo
      docPreviewDialog: false,
      docPreviewItem: null,
      docPreviewSrc: null,
      // Canvas podpisu
      signDialog: false,
      signItem: null,
      signParty: 'admin',
      submittingSign: false,
      sigDrawingFlag: false,
      sigCtxRef: null,
      sigEmptyFlag: true,
    };
  },
    watch: {
      carId:{
        async handler(){
          if(!this.carId){
            return;
          }
          try {
            this.driversEndRent=[]
        const dep = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "cars/" + this.carId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        if (dep.data.data.drivers.length > 0) {
          this.driversEndRent.push({
            id: dep.data.data.drivers[0].id,
            name: `${dep.data.data.drivers[0].firstName} ${dep.data.data.drivers[0].lastName}`,
          });
        }
        if (dep.data.data.secondDrivers.length > 0) {
          this.driversEndRent.push({
            id: dep.data.data.secondDrivers[0].id,
            name: `${dep.data.data.secondDrivers[0].firstName} ${dep.data.data.secondDrivers[0].lastName}`,
          });
        }
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
        }
      },
    options: {
      handler() {
        this.initialize();
      },
      deep: true,
    },
    search: {
      handler() {
        this.initialize();
      },
      deep: true,
    },
    activeTab(v) {
      // Tab 5 = Repozytorium → odśwież listę przy każdym wejściu.
      if (v === 5) this.loadGeneratedDocs();
      // Tab 6 = Alerty „Mam kolizję" → odśwież listę
      if (v === 6) this.loadCrashAlerts();
    },
  },
  computed: {
    carSelectOptional() {
      return [
        { id: null, text: 'Brak przypisanego pojazdu' },
        ...(this.carSelect || [])
      ];
    },
    newAlertsCount() {
      return (this.crashAlerts || []).filter(a => a.status === 'NEW').length;
    },
    // Lista kierowców do v-combobox — string-y (text + id w object). Combobox przyjmuje stringi
    // lub obiekty; my zwracamy obiekty {text, value} żeby móc rozróżnić wybór z listy od wolnego tekstu.
    driverOptions() {
      return (this.driversEndRent || []).map(d => ({ text: d.name, value: d.id }));
    },
    carOptions() {
      return (this.carSelect || []).map(c => ({ text: c.text, value: c.id }));
    },
    // 2-way bindings dla combobox-ów: zapisują albo string (wolny tekst) albo wybrany obiekt {text,value}.
    driverInput: {
      get() {
        if (this.crash.driverId) {
          const d = (this.driversEndRent || []).find(x => x.id === this.crash.driverId);
          return d ? { text: d.name, value: d.id } : null;
        }
        return this.crash.driverName || null;
      },
      set(v) {
        if (v && typeof v === 'object' && v.value) {
          this.crash.driverId = v.value;
          this.crash.driverName = '';
        } else if (typeof v === 'string' && v.trim()) {
          this.crash.driverId = null;
          this.crash.driverName = v.trim();
        } else {
          this.crash.driverId = null;
          this.crash.driverName = '';
        }
      },
    },
    carInput: {
      get() {
        if (this.carId) {
          const c = (this.carSelect || []).find(x => x.id === this.carId);
          return c ? { text: c.text, value: c.id } : null;
        }
        return this.crash.carPlate || null;
      },
      set(v) {
        if (v && typeof v === 'object' && v.value) {
          this.carId = v.value;
          this.crash.carPlate = '';
        } else if (typeof v === 'string' && v.trim()) {
          this.carId = null;
          this.crash.carPlate = v.trim();
        } else {
          this.carId = null;
          this.crash.carPlate = '';
        }
      },
    },
    rentDays() {
      const from = this.rentCalc.from ? new Date(this.rentCalc.from) : null;
      const to = this.rentCalc.to ? new Date(this.rentCalc.to) : null;
      if (!from || !to || isNaN(from.getTime()) || isNaN(to.getTime())) return 0;
      const ms = to.getTime() - from.getTime();
      if (ms < 0) return 0;
      return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
    },
    rentPerVehicle() {
      return this.rentDays * this.parseRate(this.rentCalc.dailyRate);
    },
    rentTotal() {
      const v = Math.max(1, Number(this.rentCalc.vehicles) || 1);
      return this.rentPerVehicle * v;
    },
    rentError() {
      if (!this.rentCalc.from || !this.rentCalc.to) return '';
      const from = new Date(this.rentCalc.from);
      const to = new Date(this.rentCalc.to);
      if (isNaN(from.getTime()) || isNaN(to.getTime())) return 'Nieprawidłowe daty';
      if (to < from) return 'Data "Do" nie może być wcześniejsza niż "Od"';
      if (this.parseRate(this.rentCalc.dailyRate) < 0) return 'Stawka nie może być ujemna';
      return '';
    },
    rentValid() {
      return this.rentDays > 0 && this.parseRate(this.rentCalc.dailyRate) > 0 && !this.rentError;
    },
    parsedRate() {
      return this.parseRate(this.rentCalc.dailyRate);
    },
    wizardStepLabel() {
      const labels = { 1: 'Kategoria', 2: 'Pojazd i kierowca', 3: 'Ubezpieczyciel', 4: 'Opis', 5: 'Dokumenty', 6: 'Podsumowanie' };
      return labels[this.wizardStep] || '';
    },
    canGoNext() {
      switch (this.wizardStep) {
        case 1:
          // Numer szkody jest opcjonalny na początku zgłaszania (czasem nadawany dopiero przez TU).
          return !!(this.crash.category && this.crash.date);
        case 2:
          // Szkoda rzeczowa: wymagany przedmiot, kierowca opcjonalny.
          if (this.crash.category === 'rzeczowe') return !!(this.crash.subject && this.crash.subject.trim());
          // Wystarczy ID kierowcy z bazy ALBO wolny tekst (driverName).
          return !!(this.crash.driverId || (this.crash.driverName && this.crash.driverName.trim()));
        case 3:
          return !!(this.crash.insuranceInfo && this.crash.insuranceInfo.trim());
        case 4:
          return !!(this.crash.description && this.crash.description.trim());
        case 5:
          return true; // opcjonalne
        default:
          return true;
      }
    },
    filteredHistory() {
      const q = (this.historySearch || '').trim().toLowerCase();
      if (!q) return this.rentHistory;
      return this.rentHistory.filter(h =>
        (h.plate || '').toLowerCase().includes(q) ||
        (h.crashNumber || '').toLowerCase().includes(q)
      );
    },
  },
  async created() {
    this.loadRentHistory();
    await this.initialize();
    await this.getCarsSelect();
    // Repozytorium dokumentów — wstępne pobranie żeby licznik na zakładce był aktualny.
    this.loadGeneratedDocs();
    // Alerty — pobierz aktywne (NEW) na starcie żeby badge na zakładce świecił
    this.loadCrashAlerts();
  },
  methods: {
    filteredCrashes(category) {
      const list = this.carCrashes || [];
      // Fallback na "firmowe" jeśli wpis nie ma category (stare rekordy przed migracją)
      return list.filter(c => (c.category || 'firmowe') === category);
    },

    // ===== Document generator =====
    resetGenForm() {
      this.genStep = 1;
      this.genForm = {
        caseType: 'CESJA',
        cedent: '',
        city: 'Bydgoszcz',
        documentDate: '',
        signedDate: '',
        damageDate: '',
        vehicleBrand: '',
        registrationNumber: '',
        rentalAgreementNumber: '',
        types: ['CESJA'],
      };
    },
    async submitGenerate() {
      if (!this.genForm.types || this.genForm.types.length === 0) return;
      this.generating = true;
      try {
        for (const type of this.genForm.types) {
          const payload = {
            type,
            caseType: this.genForm.caseType,
            data: {
              city: this.genForm.city,
              documentDate: this.formatPlDate(this.genForm.documentDate),
              signedDate: this.formatPlDate(this.genForm.signedDate),
              damageDate: this.formatPlDate(this.genForm.damageDate),
              vehicleBrand: this.genForm.vehicleBrand,
              registrationNumber: this.genForm.registrationNumber,
              rentalAgreementNumber: this.genForm.rentalAgreementNumber,
              cedent: this.genForm.cedent,
            },
          };
          await axios.post(
            process.env.VUE_APP_ROOT_API + 'document-generator',
            payload,
            { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
          );
        }
        Swal.fire({ icon: 'success', title: 'Wygenerowano', text: 'PDF dostępny w zakładce Repozytorium', timer: 2000, showConfirmButton: false });
        this.resetGenForm();
        await this.loadGeneratedDocs();
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Błąd', text: e.response?.data?.message || 'Nie udało się wygenerować' });
      }
      this.generating = false;
    },
    formatPlDate(iso) {
      if (!iso) return '';
      const [y, m, d] = String(iso).split('-');
      if (!y || !m || !d) return iso;
      return `${d}.${m}.${y}`;
    },
    async loadGeneratedDocs() {
      this.loadingDocs = true;
      try {
        const params = {};
        if (this.docFilters.caseType) params.caseType = this.docFilters.caseType;
        if (this.docFilters.status) params.status = this.docFilters.status;
        const res = await axios.get(process.env.VUE_APP_ROOT_API + 'document-generator', {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          params,
        });
        this.generatedDocs = res.data || [];
      } catch (e) {
        console.error(e);
      }
      this.loadingDocs = false;
    },
    docStatusColor(status) {
      switch (status) {
        case 'DRAFT': return 'grey';
        case 'SIGNED_ADMIN': return 'orange';
        case 'SIGNED_BOTH': return 'success';
        default: return 'grey';
      }
    },
    async downloadDoc(item) {
      if (!item.fileId) return;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + 'aws/download-file/' + item.fileId,
          {},
          {
            headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
            responseType: 'blob',
          },
        );
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = (item.number || 'dokument').replace(/\//g, '_') + '.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Błąd pobierania' });
      }
    },

    // ===== Podgląd PDF z aktualnych pól (bez zapisu) — krok 4 generatora =====
    async openPreview() {
      this.loadingPreview = true;
      try {
        if (this.previewUrl) {
          URL.revokeObjectURL(this.previewUrl);
          this.previewUrl = null;
        }
        for (const type of (this.genForm.types || ['CESJA'])) {
          const payload = {
            type,
            data: {
              city: this.genForm.city,
              documentDate: this.formatPlDate(this.genForm.documentDate),
              signedDate: this.formatPlDate(this.genForm.signedDate),
              damageDate: this.formatPlDate(this.genForm.damageDate),
              vehicleBrand: this.genForm.vehicleBrand,
              registrationNumber: this.genForm.registrationNumber,
              rentalAgreementNumber: this.genForm.rentalAgreementNumber,
              cedent: this.genForm.cedent,
            },
          };
          const res = await axios.post(
            process.env.VUE_APP_ROOT_API + 'document-generator/preview',
            payload,
            {
              headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
              responseType: 'blob',
            },
          );
          this.previewUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
          break; // pokazujemy tylko pierwszy podgląd; reszta dokumentów dochodzi w przyszłości
        }
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Błąd podglądu', text: e.response?.data?.message || e.message });
      }
      this.loadingPreview = false;
    },

    // ===== Podgląd istniejącego dokumentu z repo =====
    async openDocPreview(item) {
      this.docPreviewItem = item;
      this.docPreviewDialog = true;
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + 'document-generator/' + item.id + '/preview',
          {
            headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
            responseType: 'blob',
          },
        );
        this.docPreviewSrc = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Błąd podglądu' });
      }
    },

    // ===== Podpis canvas (admin / klient na tablecie) =====
    openSignDialog(item, party) {
      this.signItem = item;
      this.signParty = party;
      this.signDialog = true;
      this.sigEmptyFlag = true;
      this.$nextTick(() => this.initSignatureCanvas());
    },
    initSignatureCanvas() {
      const c = this.$refs.sigCanvas;
      if (!c) return;
      // ustaw rozdzielczość zgodną z DPI urządzenia, żeby kreska była ostra na tablecie
      const ratio = window.devicePixelRatio || 1;
      const rect = c.getBoundingClientRect();
      c.width = rect.width * ratio;
      c.height = rect.height * ratio;
      const ctx = c.getContext('2d');
      ctx.scale(ratio, ratio);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2.4;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, rect.width, rect.height);
      this.sigCtxRef = ctx;
    },
    sigPos(e) {
      const c = this.$refs.sigCanvas;
      const r = c.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    },
    sigPosTouch(e) {
      const c = this.$refs.sigCanvas;
      const r = c.getBoundingClientRect();
      const t = e.touches[0];
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    },
    sigStart(e) {
      this.sigDrawingFlag = true;
      const p = this.sigPos(e);
      this.sigCtxRef.beginPath();
      this.sigCtxRef.moveTo(p.x, p.y);
    },
    sigStartTouch(e) {
      this.sigDrawingFlag = true;
      const p = this.sigPosTouch(e);
      this.sigCtxRef.beginPath();
      this.sigCtxRef.moveTo(p.x, p.y);
    },
    sigDraw(e) {
      if (!this.sigDrawingFlag) return;
      const p = this.sigPos(e);
      this.sigCtxRef.lineTo(p.x, p.y);
      this.sigCtxRef.stroke();
      this.sigEmptyFlag = false;
    },
    sigDrawTouch(e) {
      if (!this.sigDrawingFlag) return;
      const p = this.sigPosTouch(e);
      this.sigCtxRef.lineTo(p.x, p.y);
      this.sigCtxRef.stroke();
      this.sigEmptyFlag = false;
    },
    sigStop() {
      this.sigDrawingFlag = false;
    },
    clearSignature() {
      const c = this.$refs.sigCanvas;
      if (!c) return;
      const rect = c.getBoundingClientRect();
      this.sigCtxRef.fillStyle = '#fff';
      this.sigCtxRef.fillRect(0, 0, rect.width, rect.height);
      this.sigEmptyFlag = true;
    },
    async submitSignature() {
      if (this.sigEmptyFlag) {
        Swal.fire({ icon: 'warning', title: 'Brak podpisu' });
        return;
      }
      this.submittingSign = true;
      try {
        const c = this.$refs.sigCanvas;
        const dataUrl = c.toDataURL('image/png');
        await axios.post(
          process.env.VUE_APP_ROOT_API + 'document-generator/' + this.signItem.id + '/sign',
          { signature: dataUrl, party: this.signParty },
          { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } },
        );
        this.signDialog = false;
        await this.loadGeneratedDocs();
        Swal.fire({ icon: 'success', title: 'Podpisano', timer: 1500, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Błąd podpisu', text: e.response?.data?.message || e.message });
      }
      this.submittingSign = false;
    },
    async deleteDoc(item) {
      const ok = await Swal.fire({ icon: 'warning', title: 'Usunąć dokument?', showCancelButton: true, confirmButtonText: 'Usuń' });
      if (!ok.isConfirmed) return;
      try {
        await axios.delete(process.env.VUE_APP_ROOT_API + 'document-generator/' + item.id, {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        await this.loadGeneratedDocs();
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Błąd' });
      }
    },
    countByCategory(category) {
      return this.filteredCrashes(category).length;
    },

    // ===== Alerty „Mam kolizję" =====
    async loadCrashAlerts() {
      this.loadingAlerts = true;
      try {
        const params = new URLSearchParams({ take: '50', skip: '1' });
        if (this.alertsFilter.status) params.set('status', this.alertsFilter.status);
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'driver-cars/crash-alerts?' + params.toString(),
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.crashAlerts = res.data?.data?.result || [];
      } catch (e) {
        console.error('Failed to load crash alerts:', e);
        Swal.fire('Błąd', e?.response?.data?.message?.toString?.() || 'Nie udało się załadować alertów', 'error');
      } finally {
        this.loadingAlerts = false;
      }
    },
    async setAlertStatus(alert, newStatus) {
      try {
        await axios({
          method: 'patch',
          url: process.env.VUE_APP_ROOT_API + `driver-cars/crash-alerts/${alert.id}/status`,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { status: newStatus },
        });
        await this.loadCrashAlerts();
      } catch (e) {
        Swal.fire('Błąd', e?.response?.data?.message?.toString?.() || 'Nie udało się zmienić statusu', 'error');
      }
    },
    async convertAlertToCrash(alert) {
      // Otwórz wizard nowej szkody, prefilluj kierowcę i auto z alertu, oznacz alert jako CONTACTED.
      // Po faktycznym zapisaniu szkody admin sam może wrócić tu i kliknąć Zamknij.
      const ok = await Swal.fire({
        title: 'Konwertuj na szkodę?',
        text: 'Otwórz pełny formularz zgłoszenia szkody. Alert zostanie oznaczony jako "Skontaktowano".',
        icon: 'question', showCancelButton: true,
        confirmButtonText: 'Otwórz formularz', cancelButtonText: 'Anuluj',
      });
      if (!ok.isConfirmed) return;
      // Wstępnie przepnij na CONTACTED — formalna szkoda i tak utworzy się w wizardzie
      try {
        await axios({
          method: 'patch',
          url: process.env.VUE_APP_ROOT_API + `driver-cars/crash-alerts/${alert.id}/status`,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { status: 'CONTACTED' },
        });
      } catch (e) {
        // Niekrytyczne — pokaż formularz nawet jeśli status się nie zapisze
        console.warn('crash-alerts status update failed (non-blocking):', e);
      }
      // Pre-fill wizard: kategoria firmowe + przepiętne dane jeżeli są
      this.crash.category = 'firmowe';
      if (alert.driver) {
        this.driverInput = { text: `${alert.driver.firstName} ${alert.driver.lastName}`, value: alert.driver.id };
      }
      if (alert.car) {
        this.crash.carId = alert.car.id;
      }
      if (alert.note) {
        this.crash.description = alert.note;
      }
      this.crash.date = (alert.createdAt || '').toString().slice(0, 10);
      this.wizardStep = 1;
      this.dialogCreate = true;
      this.activeTab = 0;
    },
    alertStatusColor(status) {
      return ({
        NEW: 'error',
        CONTACTED: 'info',
        CLOSED: 'success',
        CONVERTED: 'warning',
      })[status] || 'grey';
    },
    alertStatusLabel(status) {
      return ({
        NEW: 'Nowy',
        CONTACTED: 'Skontaktowano',
        CLOSED: 'Zamknięty',
        CONVERTED: 'Konwertowany',
      })[status] || status;
    },
    formatDateTime(d) {
      if (!d) return '—';
      return dayjs(d).format('DD.MM.YYYY HH:mm');
    },
    openCreateForCategory(category) {
      this.crash.category = category;
      this.wizardStep = 1;
      this.dialogCreate = true;
    },
    closeWizard() {
      this.dialogCreate = false;
      this.wizardStep = 1;
    },
    categoryLabel(value) {
      const o = this.categoryOptions.find(x => x.value === value);
      return o ? o.text : value || '—';
    },
    driverDisplay(id) {
      if (!id) return '—';
      const d = (this.driversEndRent || []).find(x => x.id === id);
      return d ? d.name : id;
    },
    carDisplay(id) {
      if (!id) return 'Brak przypisanego pojazdu';
      const c = (this.carSelect || []).find(x => x.id === id);
      return c ? c.text : id;
    },
    resetRentCalc() {
      this.rentCalc = { from: '', to: '', dailyRate: null, vehicles: 1, plate: '', crashNumber: '' };
    },
    loadRentHistory() {
      try {
        const raw = localStorage.getItem('btp-rent-calc-history');
        this.rentHistory = raw ? JSON.parse(raw) : [];
      } catch (e) {
        this.rentHistory = [];
      }
    },
    persistRentHistory() {
      try {
        localStorage.setItem('btp-rent-calc-history', JSON.stringify(this.rentHistory));
      } catch (e) { /* ignore quota */ }
    },
    saveRentCalc() {
      if (!this.rentValid) return;
      const entry = {
        id: 'rc_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
        savedAt: new Date().toISOString(),
        plate: (this.rentCalc.plate || '').trim(),
        crashNumber: (this.rentCalc.crashNumber || '').trim(),
        from: this.rentCalc.from,
        to: this.rentCalc.to,
        days: this.rentDays,
        dailyRate: this.parsedRate,
        vehicles: Number(this.rentCalc.vehicles) || 1,
        perVehicle: this.rentPerVehicle,
        total: this.rentTotal,
      };
      this.rentHistory.unshift(entry);
      // Maksymalnie 100 wpisów żeby nie zapychać localStorage
      if (this.rentHistory.length > 100) this.rentHistory.length = 100;
      this.persistRentHistory();
      Swal.fire({ icon: 'success', title: 'Zapisano obliczenie', timer: 1200, showConfirmButton: false });
    },
    loadHistoryEntry(h) {
      this.rentCalc = {
        from: h.from,
        to: h.to,
        dailyRate: h.dailyRate,
        vehicles: h.vehicles,
        plate: h.plate || '',
        crashNumber: h.crashNumber || '',
      };
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    copyHistoryEntry(h) {
      const lines = [
        'Najem pojazdu zastępczego',
        h.plate ? `Nr rej.: ${h.plate}` : null,
        h.crashNumber ? `Nr szkody: ${h.crashNumber}` : null,
        `Okres: ${h.from} – ${h.to}`,
        `Liczba dni: ${h.days}`,
        `Stawka dobowa: ${this.formatPln(h.dailyRate)}`,
        `Liczba pojazdów: ${h.vehicles}`,
        `Kwota za pojazd: ${this.formatPln(h.perVehicle)}`,
        `SUMA: ${this.formatPln(h.total)}`,
      ].filter(Boolean).join('\n');
      navigator.clipboard?.writeText(lines).then(
        () => Swal.fire({ icon: 'success', title: 'Skopiowano', timer: 1200, showConfirmButton: false }),
        () => Swal.fire('Nie udało się skopiować', lines, 'info'),
      );
    },
    deleteHistoryEntry(id) {
      this.rentHistory = this.rentHistory.filter(h => h.id !== id);
      this.persistRentHistory();
    },
    async clearRentHistory() {
      const res = await Swal.fire({
        icon: 'warning',
        title: 'Wyczyścić całą historię obliczeń?',
        showCancelButton: true,
        confirmButtonText: 'Tak, wyczyść',
        cancelButtonText: 'Anuluj',
      });
      if (res.isConfirmed) {
        this.rentHistory = [];
        this.persistRentHistory();
      }
    },
    formatSavedDate(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleString('pl-PL', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit',
        });
      } catch (e) { return iso; }
    },
    parseRate(val) {
      if (val === null || val === undefined || val === '') return 0;
      const n = parseFloat(String(val).replace(',', '.'));
      return isNaN(n) ? 0 : n;
    },
    formatPln(v) {
      const n = Number(v) || 0;
      return n.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async copyRentSummary() {
      if (!this.rentValid) return;
      const lines = [
        'Najem pojazdu zastępczego',
        `Okres: ${this.rentCalc.from} – ${this.rentCalc.to}`,
        `Liczba dni: ${this.rentDays}`,
        `Stawka dobowa: ${this.formatPln(this.parsedRate)}`,
        `Liczba pojazdów: ${this.rentCalc.vehicles}`,
        `Kwota za pojazd: ${this.formatPln(this.rentPerVehicle)}`,
        `SUMA: ${this.formatPln(this.rentTotal)}`,
      ];
      const text = lines.join('\n');
      try {
        await navigator.clipboard.writeText(text);
        Swal.fire({ icon: 'success', title: 'Skopiowano do schowka', timer: 1400, showConfirmButton: false });
      } catch (e) {
        Swal.fire('Nie udało się skopiować', text, 'info');
      }
    },
    async toggleLoading(type) {
      const toggle = type;
      localStorage.setItem("loading", toggle);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
    async saveCrash() {
      this.savingWizard = true;
      await this.toggleLoading(true);
      try {
        const formData = new FormData();
        this.crash.photos?.forEach((file) => {
          try {
            formData.append(`file`, file, file.name);
          } catch (e) {
            console.log("Missing Image");
          }
        });
        formData.append("incidentDate", this.crash.date);
        formData.append("description", this.crash.description);
        formData.append("insuranceInfo", this.crash.insuranceInfo);
        if (this.crash.incidentNumber) {
          formData.append("caseNumber", this.crash.incidentNumber);
        }
        formData.append("category", this.crash.category || "firmowe");
        if (this.crash.category === 'rzeczowe' && this.crash.subject) {
          formData.append("subject", this.crash.subject.trim());
        }
        if (this.carId) {
          formData.append("carId", this.carId);
        } else if (this.crash.carPlate) {
          formData.append("carPlate", this.crash.carPlate);
        }
        if (this.crash.driverId) {
          formData.append("driverId", this.crash.driverId);
        } else if (this.crash.driverName) {
          formData.append("driverName", this.crash.driverName);
        }
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "cars-history/crash",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        this.skip = 1;
        await this.toggleLoading(false);
        await this.initialize();
this.selectedOption = null;
this.carId = null;
        this.crash.date = null;
        this.crash.description = null;
        this.crash.insuranceInfo = null;
        this.crash.incidentNumber = null;
        this.crash.driverId = null;
        this.crash.category = "firmowe";
        this.crash.subject = "";
        this.crash.photos = [];
        this.crash.previews = [];
        this.dialogCreate = false;
        this.wizardStep = 1;
        this.savingWizard = false;
      } catch (e) {
        this.savingWizard = false;
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async timelineCrash(id) {
      location.href = `/crash-history?id=${id}`;
    },
    async infoCrash(id) {
      await this.toggleLoading(true);
      try {
        const crash = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "cars-history/crash/" + id,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.formData = crash.data.data;
        this.formData.incidentDate = dayjs(crash.data.data.incidentDate).format('YYYY-MM-DD');
        this.formData.files = crash.data.data.files || [];
        this.currentCrashId = crash.data.data.id;
        this.dialog = true;
        await this.toggleLoading(false);
      } catch (e) {
        await this.toggleLoading(false);
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
    },
    async initialize() {
      this.loading = true;
          if (this.search.trim().length > 0) {
        this.options.page = 1;
      }
      try {
        const crashes = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `cars-history/crash?take=${this.options.itemsPerPage}&skip=${this.options.page}&search=${this.search}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });

        if (!crashes || !crashes.data) {
          console.error('No response or response data:', crashes);
          throw new Error('Brak odpowiedzi z serwera');
        }

        if (!crashes.data.data || !crashes.data.data.result) {
          console.error('Unexpected API response structure:', crashes.data);
          throw new Error('Nieprawidłowa struktura odpowiedzi API');
        }

        this.carCrashes = crashes.data.data.result.map((crash) => {
          const statusDisplay = this.getStatusDisplay(crash.status || (crash.isFixed ? 'fixed' : 'pending'));
          return {
            id: crash.id || '',
            description: crash.description || '',
            incidentDate: crash.incidentDate ? dayjs(crash.incidentDate).format("YYYY-MM-DD") : '',
            caseNumber: crash.caseNumber || '',
            insuranceInfo: crash.insuranceInfo || '',
            carPlate: crash.car && crash.car.plate ? crash.car.plate : 'Brak pojazdu',
            status: statusDisplay,
            hasFiles: crash.files && Array.isArray(crash.files) && crash.files.length > 0,
          };
        });
         this.totalItems = crashes.data.data.count || 0;
      } catch (e) {
        console.error('Error fetching crashes:', e);
        const errorMessage = e.response?.data?.message || e.message || 'Nieznany błąd podczas pobierania szkód';
        Swal.fire("Błąd", errorMessage.toString(), "error");
      }
      this.loading = false;
    },
    async getCarsSelect(){
      try {
        const cars = await axios({
          method: "get",
          url:
            process.env.VUE_APP_ROOT_API +
            `cars?take=1000&skip=1&search=`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });

        if (!cars || !cars.data) {
          console.error('No response or response data:', cars);
          throw new Error('Brak odpowiedzi z serwera');
        }

        if (!cars.data.data || !cars.data.data.result) {
          console.error('Unexpected API response structure:', cars.data);
          this.carSelect = [];
          return;
        }

        this.carSelect = cars.data.data.result.map((car) => {
          return {
            id: car.id,
            text: car.make +' '+ car.plate,
          };
        });
      } catch (e) {
        console.error('Error fetching cars:', e);
        const errorMessage = e.response?.data?.message || e.message || 'Błąd podczas pobierania pojazdów';
        Swal.fire("Błąd", errorMessage.toString(), "error");
        this.carSelect = [];
      }
    },
    async submitForm() {
      // Validate the form
      if (this.$refs.form.validate()) {
        try {
          await this.toggleLoading(true);

          // If there are new files to upload, use FormData
          if (this.formData.newFiles && this.formData.newFiles.length > 0) {
            const formData = new FormData();

            // Add new files
            this.formData.newFiles.forEach((file) => {
              try {
                formData.append(`file`, file, file.name);
              } catch (e) {
                console.log("Error adding file:", e);
              }
            });

            // Add other form data
            formData.append("incidentDate", this.formData.incidentDate);
            formData.append("description", this.formData.description);
            formData.append("insuranceInfo", this.formData.insuranceInfo);
            formData.append("caseNumber", this.formData.caseNumber);
            if (this.formData.category === 'rzeczowe') formData.append("subject", this.formData.subject || "");
            formData.append("status", this.formData.status);

            await axios({
              method: "patch",
              url:
                process.env.VUE_APP_ROOT_API +
                "cars-history/crash/" +
                this.currentCrashId,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
                "Content-Type": "multipart/form-data",
              },
              data: formData,
            });
          } else {
            // No new files, send regular JSON data
            await axios({
              method: "patch",
              url:
                process.env.VUE_APP_ROOT_API +
                "cars-history/crash/" +
                this.currentCrashId,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
              data: {
                incidentDate: this.formData.incidentDate,
                description: this.formData.description,
                insuranceInfo: this.formData.insuranceInfo,
                caseNumber: this.formData.caseNumber,
                status: this.formData.status,
                ...(this.formData.category === 'rzeczowe' ? { subject: this.formData.subject || "" } : {}),
              },
            });
          }

          this.closeDialog();
          await this.toggleLoading(false);
          await this.initialize();
        } catch (e) {
          await this.toggleLoading(false);
          console.log(e);
          Swal.fire("Błąd", e.response.data.message.toString(), "error");
        }
      }
    },
    closeDialog() {
      this.dialog = false;
      this.currentCrashId = undefined;
      this.formData.newFiles = [];
      this.formData.newFilePreviews = [];
      this.$refs.form.resetValidation(); // Reset form validation
    },
    getStatusDisplay(status) {
      switch (status) {
        case 'pending':
          return "Oczekuje";
        case 'in_progress':
          return "W trakcie 🔄";
        case 'fixed':
          return "Zlikwidowana ✅";
        default:
          return "Oczekuje";
      }
    },
    previewFiles(files) {
      this.crash.previews = [];
      if (files && files.length > 0) {
        files.forEach((file) => {
          if (file instanceof File) {
            const reader = new FileReader();
            reader.onload = (e) => {
              const preview = {
                name: file.name,
                url: e.target.result,
                type: file.type.startsWith('image/') ? 'image' :
                      file.type === 'application/pdf' ? 'pdf' : 'other'
              };
              this.crash.previews.push(preview);
            };
            reader.readAsDataURL(file);
          }
        });
      }
    },
    async downloadCrashFiles(crashId) {
      try {
        await this.toggleLoading(true);
        const files = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "cars-history/crash/" + crashId + "/files",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });

        if (files.data.data && files.data.data.length > 0) {
          for (const fileInfo of files.data.data) {
            const file = await axios({
              method: "post",
              url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + fileInfo.id,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
              responseType: "blob",
            });
            let blob = new Blob([file.data], { type: file.data.type }),
                url = window.URL.createObjectURL(blob);
            window.open(url);
          }
        }
        await this.toggleLoading(false);
      } catch (e) {
        await this.toggleLoading(false);
        console.log(e);
        Swal.fire("Błąd", "Nie udało się pobrać plików", "error");
      }
    },
    async downloadSingleFile(fileId) {
      try {
        const file = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + fileId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          responseType: "blob",
        });
        let blob = new Blob([file.data], { type: file.data.type }),
            url = window.URL.createObjectURL(blob);
        window.open(url);
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", "Nie udało się pobrać pliku", "error");
      }
    },
    previewEditFiles(files) {
      this.formData.newFilePreviews = [];
      if (files && files.length > 0) {
        files.forEach((file) => {
          if (file instanceof File) {
            const reader = new FileReader();
            reader.onload = (e) => {
              const preview = {
                name: file.name,
                url: e.target.result,
                type: file.type.startsWith('image/') ? 'image' :
                      file.type === 'application/pdf' ? 'pdf' : 'other'
              };
              this.formData.newFilePreviews.push(preview);
            };
            reader.readAsDataURL(file);
          }
        });
      }
    },
  },
};
</script>

<style scoped>
.car-crash-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.table-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #2A3142;
}
.filter-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(84, 120, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}
.filter-title {
  font-size: 15px;
  font-weight: 600;
  color: #E6EAF2;
}
.action-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}
.action-pill--primary { background: #5478FF; color: #FFFFFF; }
.action-pill--primary:hover { background: #3F62E8; }
.car-crash-page >>> .v-data-table {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 12px;
  border: 1px solid #2A3142;
}
.car-crash-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.car-crash-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}
.car-crash-page >>> .v-data-table tbody tr:hover {
  background: rgba(84, 120, 255, 0.06) !important;
}
.car-crash-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.car-crash-page >>> .v-text-field input,
.car-crash-page >>> .v-text-field textarea,
.car-crash-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.car-crash-page >>> .v-label {
  color: #94A0B8 !important;
}
.rent-calc-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: .2px;
}
.rent-calc-kpi {
  background: #f5f7fb;
  border: 1px solid #e2e5ea;
  border-radius: 10px;
  padding: 14px 16px;
  text-align: center;
}
.rent-calc-kpi-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0,0,0,.6);
  text-transform: uppercase;
  letter-spacing: .5px;
}
.rent-calc-kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin-top: 4px;
  font-variant-numeric: tabular-nums;
}
.rent-calc-kpi-sub { font-size: 11px; color: #888; margin-top: 2px; }
.rent-calc-kpi--total { background: #eef4ff; border-color: #b9cff9; }
.rent-calc-kpi--total .rent-calc-kpi-value { color: #1e40af; }
.rent-calc-error { color: #c62828; font-size: 13px; display: flex; align-items: center; gap: 4px; }
.rent-calc-actions { display: flex; justify-content: flex-end; gap: 8px; }

.crashes-tabs {
  border-bottom: 1px solid #2A3142;
  background: #232838 !important;
  border-radius: 12px 12px 0 0;
  border: 1px solid #2A3142;
  border-bottom: none;
  padding: 0 8px;
}
.crashes-tabs >>> .v-tab {
  color: #94A0B8 !important;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-transform: none;
  min-width: 0;
}
.crashes-tabs >>> .v-tab--active {
  color: #5478FF !important;
}
.crashes-tabs >>> .v-tabs-slider {
  background-color: #5478FF !important;
}
.crashes-tabs-items { background: transparent !important; }

/* Historia obliczeń */
.rent-history-head {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: #E6EAF2;
  flex-wrap: wrap;
  gap: 6px;
}
.rent-history-empty {
  padding: 40px 20px;
  text-align: center;
  color: #94A0B8;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.rent-history-table th {
  text-transform: uppercase;
  letter-spacing: .5px;
  font-size: 11px !important;
  color: #94A0B8 !important;
}
.rent-history-table td { font-size: 13px; vertical-align: middle; color: #E6EAF2; }
.rent-history-date { white-space: nowrap; color: #94A0B8; font-variant-numeric: tabular-nums; }
.rent-history-period { white-space: nowrap; font-variant-numeric: tabular-nums; }
.rent-history-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(84, 120, 255, 0.18);
  color: #5478FF;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  margin: 1px 2px 1px 0;
}
.rent-history-tag--muted { background: rgba(255, 167, 38, 0.15); color: #FFB74D; }
.rent-history-sum { font-weight: 700; color: #5478FF; }

.crashes-tab-count {
  margin-left: 6px;
  background: rgba(148, 160, 184, 0.18);
  color: #94A0B8;
  border-radius: 999px;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 700;
  min-width: 22px;
  text-align: center;
}
.v-tab--active .crashes-tab-count { background: rgba(84, 120, 255, 0.18); color: #5478FF; }
.crashes-tab-count--alert {
  background: #ef4444 !important;
  color: #fff !important;
  animation: pulse-red 1.6s ease-in-out infinite;
}
@keyframes pulse-red {
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6); }
  50% { box-shadow: 0 0 0 6px rgba(239, 68, 68, 0); }
}

/* Kreator szkody */
.crash-wizard-title {
  background: #1976d2;
  color: #ffffff !important;
  font-size: 16px;
  font-weight: 700;
  border-bottom: 1px solid rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  padding: 14px 20px;
}
.crash-wizard-title .v-icon {
  color: #ffffff !important;
}
.crash-wizard-title .v-btn .v-icon {
  color: #ffffff !important;
}
.crash-stepper { box-shadow: none !important; }
.crash-stepper ::v-deep .v-stepper__content { padding: 20px 28px; }
.wiz-hint {
  background: #eef4ff;
  border-left: 3px solid #3b82f6;
  padding: 10px 14px;
  border-radius: 6px;
  color: #1e3a8a;
  font-size: 13px;
  margin-bottom: 16px;
}
.wiz-radio ::v-deep .v-input--selection-controls__input { margin-right: 4px; }

.wiz-summary {
  background: #f7f9fc;
  border: 1px solid #e2e5ea;
  border-radius: 8px;
  overflow: hidden;
}
.wiz-summary-row {
  display: flex;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #e8ebef;
  align-items: center;
}
.wiz-summary-row:last-child { border-bottom: 0; }
.wiz-summary-row--block { flex-direction: column; align-items: flex-start; }
.wiz-summary-label {
  flex: 0 0 180px;
  color: rgba(0,0,0,.6);
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .5px;
}
.wiz-summary-value {
  color: rgba(0,0,0,.87);
  font-size: 14px;
  font-weight: 500;
}
.wiz-summary-desc {
  margin-top: 6px;
  color: rgba(0,0,0,.87);
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  width: 100%;
}
.crash-wizard-actions {
  padding: 12px 20px;
  border-top: 1px solid #e3e6ec;
  background: #fafbfc;
}
</style>
